import { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { SubmissionError } from "redux-form";
import _, { isObject } from "underscore";
import URI from "urijs";

import type { RegisterValues } from "@js/apps/auth/actions";
import { fetchCurrentUser, register } from "@js/apps/auth/actions";
import { useUser } from "@js/apps/common/hooks";
import { Snackbar } from "@js/components/snackbar";
import { globalConfig } from "@js/constants";
import { getValuesWithReCaptchaCode } from "@js/forms/utils";
import { useAppDispatch, useNavigate, useQueryParams } from "@js/hooks";
import { LocalStorage, SessionStorage } from "@js/services";
import { iframePostMessage, isInIframe } from "@js/utils";

import { useNewClientSignUpInvitation } from "../new-client-signup-invitation";
import { useNewFreelancerRegisterPrefillData } from "../use-new-freelancer-register-prefill-data";
import { useNewFreelancerSignUpInvitation } from "../use-new-freelancer-signup-invitation";

import {
  getFinishSignupRedirectUrl,
  getInitialValues,
  getSubmissionErrorOnCreateAccountStep,
  getValuesWithValidatePhoneNumber,
  updateLocalStorageData,
} from "./helpers";

const handleValidationErrorsOnCreateAccountStep = (errors: unknown) => {
  if (!_.isObject(errors)) {
    Snackbar.error("Something went wrong!");
    return;
  }

  throw new SubmissionError(getSubmissionErrorOnCreateAccountStep(errors));
};

export const useOnboardingForm = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { job_id, ...queryParams } = useQueryParams();
  const user = useUser();
  const dispatch = useAppDispatch();
  const { isClientInvitation, newClientInvitationData } =
    useNewClientSignUpInvitation();

  const { newFreelancerInvitationData, isFreelancerInvitation } =
    useNewFreelancerSignUpInvitation();
  const { freelancerRegisterPrefillData } =
    useNewFreelancerRegisterPrefillData();
  const isClientSignup = isClientInvitation;

  const redirectAfterSignUp = useCallback(
    (pathname: string) => {
      if (isInIframe()) {
        globalConfig.surpressDisplayGenericRequestError = true;
        iframePostMessage({ redirect: window.location.origin + pathname });
      } else {
        //slice removes "?" from the query string
        const url = new URI(pathname).addSearch(search.slice(1)).toString();
        return navigate(url);
      }
    },
    [navigate, search],
  );

  const getReferrer = useCallback(() => {
    return (
      LocalStorage.getItem(LocalStorage.keys.SIGN_UP_REFERRER, () => {
        return queryParams.referrer as string | undefined;
      }) || undefined
    );
  }, [queryParams.referrer]);

  const onSubmit = useCallback(
    async (values: RegisterValues) => {
      updateLocalStorageData();
      const valuesWithValidatedPhoneNumber =
        getValuesWithValidatePhoneNumber(values);

      const _values = await getValuesWithReCaptchaCode(
        valuesWithValidatedPhoneNumber,
        "sign_up",
      );

      try {
        const { email, finish_signup_url: finishSignupURL } = await dispatch(
          register({
            values: _values,
            referrer: getReferrer(),
            job_id: job_id as string | undefined,
            queryParams,
          }),
        );

        LocalStorage.setItem(
          LocalStorage.keys.REGISTRATION_EMAIL_LOCAL_STORAGE_KEY,
          email,
        );

        const isEmailVerificationOmitted = isFreelancerInvitation;
        if (isEmailVerificationOmitted) {
          SessionStorage.setItem(
            SessionStorage.keys.POST_USER_EMAIL_VERIFICATION,
            "true",
          );
        }

        const accountType = (await dispatch(fetchCurrentUser())).account_type;

        const postSignupRedirectUrl = getFinishSignupRedirectUrl({
          finishSignupURL,
          accountType,
          nextQueryParam: queryParams.next as string | undefined,
          invitationKeyQueryParam: queryParams.invitation_key as
            string | undefined,
        });

        return redirectAfterSignUp(postSignupRedirectUrl);
      } catch (err: unknown) {
        const error = isObject(err) ? err : { _error: err };

        const serverError = error.errors?._error || error._error;

        if (serverError) {
          Snackbar.error(serverError);
        }

        handleValidationErrorsOnCreateAccountStep(error.errors);
      }
    },
    [
      dispatch,
      getReferrer,
      job_id,
      queryParams,
      redirectAfterSignUp,
      isFreelancerInvitation,
    ],
  );

  const initialValues = useMemo(
    () =>
      getInitialValues(user, {
        newClientInvitationData,
        newFreelancerInvitationData,
        isClientSignup,
        freelancerRegisterPrefillData,
      }),
    [
      user,
      newClientInvitationData,
      newFreelancerInvitationData,
      isClientSignup,
      freelancerRegisterPrefillData,
    ],
  );

  return {
    formProps: {
      onSubmit,
      initialValues,
      enableReinitialize: true,
    },
    getReferrer,
  };
};
